import { useMemo, useRef, useState } from 'react';
import { track } from '../../../analytics';
import { computeDrinks, defaultSettings, DrinkKey, DrinksSettings, MomentKey, MomentSettings } from './drinksModel';

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

// Verres par personne au plus, avant de repasser à « pas servi »
export const MAX_GLASSES = 2;

// État du calculateur : invités, boissons servies à chaque moment et leurs verres
// par personne, et résultat.
export function useDrinksCalculator() {
  const [s, setS] = useState<DrinksSettings>(() => defaultSettings());
  const result = useMemo(() => computeDrinks(s), [s]);

  // Première modification du calculateur : un événement par visite de la page
  const used = useRef(false);
  const markUsed = () => {
    if (used.current) return;
    used.current = true;
    track('tool_use', { tool: 'drinks_calculator' });
  };

  // Mise à jour d'un moment à partir de son état courant (clics rapides successifs)
  const updateMoment = (key: MomentKey, fn: (m: MomentSettings) => Partial<MomentSettings>) =>
    setS((prev) => ({
      ...prev,
      moments: { ...prev.moments, [key]: { ...prev.moments[key], ...fn(prev.moments[key]) } },
    }));

  return {
    settings: s,
    result,
    setGuests: (n: number) => {
      markUsed();
      setS((prev) => ({ ...prev, guests: clamp(Math.round(n) || 0, 0, 2000) }));
    },
    // Un clic fait tourner la case : pas servi → 1 → 2 verres → pas servi…
    cycleGlasses: (key: MomentKey, d: DrinkKey) => {
      markUsed();
      updateMoment(key, (m) => {
        if (!m.served[d]) return { served: { ...m.served, [d]: true }, glasses: { ...m.glasses, [d]: 1 } };
        if (m.glasses[d] >= MAX_GLASSES) return { served: { ...m.served, [d]: false } };
        return { glasses: { ...m.glasses, [d]: Math.floor(m.glasses[d]) + 1 } };
      });
    },
  };
}

export type DrinksCalculatorState = ReturnType<typeof useDrinksCalculator>;
